import { useState } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { useDarkMode } from '@/hooks/useDarkMode';
import useScrollLock from '@/hooks/useScrollLock';

import { logger } from '@/utils/logger';
import { FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
export default function TermsAcceptanceModal({ onAccept }) {
  useScrollLock(true);
  const darkMode = useDarkMode();
  const [accepting, setAccepting] = useState(false);
  const [checked, setChecked] = useState(false);

  const handleAccept = async () => {
    if (!checked) {
      toast.error('Please accept the Terms of Service to continue');
      return;
    }

    setAccepting(true);
    try {
      const response = await api.post('/terms/accept');
      
      // Update localStorage with new user data
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      
      toast.success('Terms accepted');
      onAccept();
    } catch (error) {
      logger.error('Error accepting terms:', error);
      toast.error('Failed to accept terms. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 transition-colors"
      style={{ background: darkMode.isDark ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 0.5)' }}
    >
      <div 
        className="rounded-2xl max-w-md w-full transition-colors" 
        style={{ 
          background: darkMode.bgSecondary,
          boxShadow: darkMode.isDark ? '0 20px 60px rgba(0, 0, 0, 0.6)' : '0 20px 60px rgba(0, 0, 0, 0.3)'
        }}
      >
        
        {/* Header */}
        <div 
          className="p-6 border-b transition-colors" 
          style={{ borderColor: darkMode.isDark ? '#FED7A5' : '#E5E7EB' }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: '#9E6752' }}>
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 
                className="text-xl font-bold transition-colors" 
                style={{ fontFamily: 'Playfair Display, serif', color: darkMode.textPrimary }}
              >
                Terms of Service Updated
              </h2>
            </div>
          </div>
          <p className="text-sm transition-colors" style={{ color: darkMode.textSecondary }}>
            We've updated our Terms of Service. Please review and accept the updated terms to continue using Mementa.
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <div 
            className="rounded-lg p-4 mb-4 transition-colors" 
            style={{ background: darkMode.isDark ? 'rgba(254, 215, 165, 0.1)' : '#F9FAFB' }}
          >
            <p className="text-sm mb-2 transition-colors" style={{ color: darkMode.textSecondary }}>
              Our Terms of Service have been updated to provide you with more clarity and transparency.
            </p>
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium underline transition-colors"
              style={{ color: darkMode.isDark ? '#FED7A5' : '#9E6752' }}
            >
              Read the full Terms of Service →
            </a>
          </div>

          <div className="flex items-start gap-3 mb-6">
            <input
              type="checkbox"
              id="accept-terms"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-gray-300"
              style={{ accentColor: '#9E6752' }}
            />
            <label htmlFor="accept-terms" className="text-sm transition-colors" style={{ color: darkMode.textPrimary }}>
              I have read and agree to the updated{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium transition-colors"
                style={{ color: darkMode.isDark ? '#FED7A5' : '#9E6752' }}
              >
                Terms of Service
              </a>
            </label>
          </div>

          <Button
            onClick={handleAccept}
            disabled={accepting || !checked}
            className="w-full"
            style={{ 
              background: checked ? '#9E6752' : (darkMode.isDark ? '#4A5568' : '#ccc'), 
              color: 'white',
              cursor: checked ? 'pointer' : 'not-allowed'
            }}
          >
            {accepting ? 'Accepting...' : 'Accept & Continue'}
          </Button>

          <p className="text-xs mt-3 text-center transition-colors" style={{ color: darkMode.textSecondary }}>
            You must accept the updated terms to continue using Mementa
          </p>
        </div>
      </div>
    </div>
  );
}

